/**
 * What has to survive a trip to Microsoft's sign-in page.
 *
 * Sign-in is a full-page redirect, which discards everything in memory. Two
 * things are worth keeping:
 *
 *  - **Launched files.** A PDF double-clicked in Explorer arrives once, through
 *    the launch queue. If sign-in is needed first, the file is gone by the
 *    time the user is back -- unless it was put somewhere durable.
 *  - **The open document**, when sign-in expires mid-edit. The document itself
 *    lives on the server, so only its session id and the local file handle
 *    (for Save in place) need to be kept.
 *
 * IndexedDB rather than sessionStorage because both FileSystemFileHandle and
 * Blob are structured-cloneable into it, and neither survives JSON.
 */

import { pendingLaunches, type OpenedFile } from '../files/fileAccess'

export interface OpenDocument {
  sessionId: string
  /** Name, handle and source only; the bytes are on the server. */
  file: OpenedFile | null
  /** The revision last saved, so unsaved changes still count as unsaved. */
  savedRevision?: number | null
}

interface Carry {
  at: number
  launches: OpenedFile[]
  open: OpenDocument | null
}

const DB_NAME = 'odie'
const STORE = 'carry'
const KEY = 'pending'

// A carry older than this belongs to a sign-in that was abandoned, not the one
// that just finished. Restoring it would open a file the user has forgotten.
const MAX_AGE_MS = 10 * 60 * 1000

let openSource: (() => OpenDocument | null) | null = null

/** The editor reports what it has open, so a redirect can carry it. */
export function registerOpenDocument(source: () => OpenDocument | null): void {
  openSource = source
}

function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => request.result.createObjectStore(STORE)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function transact<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await database()
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode)
      const request = work(tx.objectStore(STORE))
      tx.oncomplete = () => resolve(request.result)
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })
  } finally {
    db.close()
  }
}

/** Persist launches and the open document before leaving for sign-in. */
export async function stashForRedirect(): Promise<void> {
  const launches = pendingLaunches()
  const open = openSource?.() ?? null
  if (!launches.length && !open) return

  const carry: Carry = {
    at: Date.now(),
    launches,
    open: open && {
      sessionId: open.sessionId,
      // Keep the handle, drop the bytes: they can be 150 MB and the server
      // already holds the document.
      file: open.file && { ...open.file, blob: new Blob() },
    },
  }
  try {
    await transact('readwrite', (store) => store.put(carry, KEY))
  } catch (err) {
    // Private windows and some policies disable IndexedDB. Signing in still
    // works; the user just has to reopen the file.
    console.warn('Could not keep open files across sign-in:', err)
  }
}

/** Take whatever the last redirect carried, once. */
export async function takeCarry(): Promise<Omit<Carry, 'at'> | null> {
  try {
    const carry = await transact<Carry | undefined>('readonly', (store) => store.get(KEY))
    if (!carry) return null
    await transact('readwrite', (store) => store.delete(KEY))
    if (Date.now() - carry.at > MAX_AGE_MS) return null
    return { launches: carry.launches, open: carry.open }
  } catch {
    return null
  }
}
